import React, { useCallback, useEffect, useRef, useState } from "react";
import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import { AddressRefinementOptions, fetchAddressSuggestions } from "../../../Services/AddressAutocompleteService";
import {
	AddressAutocompleteResponse,
	IAddressAutocompleteResult,
} from "../../../Models/AddressAutocompleteModel";
import { extractAddress } from "../../../Helpers/AddressHelpers";
import "./AddressAutocomplete.scss";

export interface IAddressAutocompleteProps {
	change: (result: IAddressAutocompleteResult) => void;
	validAddress?: (addressValid: boolean) => boolean;
	touchStart?: () => void;
	placeholder: string;
}

const optionKey = (option: AddressAutocompleteResponse) =>
	`${option.type}:${"id" in option.data ? option.data.id : option.forslagstekst}`;

export const AddressAutocomplete = ({
	change,
	placeholder = "Indtast adresse",
	validAddress,
	touchStart,
}: IAddressAutocompleteProps) => {
	const [options, setOptions] = useState<AddressAutocompleteResponse[]>([]);
	const [inputValue, setInputValue] = useState("");
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [value, setValue] = useState<AddressAutocompleteResponse | null>(null);
	const [lookupError, setLookupError] = useState("");
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const isMountedRef = useRef(true);
	const keepOpenRef = useRef(false);
	const requestIdRef = useRef(0);
	const allowOpenRef = useRef(true);

	useEffect(() => {
		return () => {
			isMountedRef.current = false;
			requestIdRef.current += 1;
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
		};
	}, []);

	const fetchSuggestions = useCallback((query: string, caretpos?: number) => {
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}

		const requestId = requestIdRef.current;
		setLoading(true);
		debounceTimerRef.current = setTimeout(async () => {
			const results = await fetchAddressSuggestions(query, caretpos);
			if (isMountedRef.current && requestId === requestIdRef.current) {
				setOptions(results);
				setLoading(false);
			}
		}, 300);
	}, []);

	const handleInputChange = (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
		setInputValue(newInputValue);

		if (reason === "reset") {
			return;
		}

		requestIdRef.current += 1;
		allowOpenRef.current = true;
		keepOpenRef.current = false;
		setLookupError("");
		setOptions([]);
		setValue(null);

		if (newInputValue.length > 1) {
			fetchSuggestions(newInputValue);
			setOpen(true);
		} else {
			if (debounceTimerRef.current) {
				clearTimeout(debounceTimerRef.current);
			}
			setOptions([]);
			setLoading(false);
			setOpen(false);
		}

		if (validAddress) {
			validAddress(false);
		}
	};

	const selectAddress = (suggestion: AddressAutocompleteResponse) => {
		const address = extractAddress(suggestion.type, suggestion.data);
		if (!address) return;
		const requestId = ++requestIdRef.current;
		if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
		setValue(suggestion);
		setInputValue(suggestion.forslagstekst);
		setOptions([]);
		setLoading(false);
		setLookupError("");
		setOpen(false);
		change({ valid: true, value: { tekst: suggestion.forslagstekst, adresse: address } });
		setTimeout(() => {
			if (isMountedRef.current && requestId === requestIdRef.current) validAddress?.(true);
		}, 0);
	};

	const handleChange = async (
		_event: React.SyntheticEvent,
		newValue: AddressAutocompleteResponse | null
	) => {
		if (!newValue) return;
		if (extractAddress(newValue.type, newValue.data)) {
			selectAddress(newValue);
			return;
		}

		const requestId = ++requestIdRef.current;
		if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
		validAddress?.(false);
		keepOpenRef.current = true;
		allowOpenRef.current = true;
		setValue(null);
		setOptions([]);
		setLookupError("");
		setInputValue(newValue.forslagstekst);

		let refinement: AddressRefinementOptions | undefined;
		if (newValue.type === "adgangsadresse" && newValue.data.id?.trim()) {
			refinement = { startFrom: "adresse", accessAddressId: newValue.data.id };
		} else if (newValue.type !== "vejnavn") {
			setLookupError("Kunne ikke hente adressen. Vælg adressen igen.");
			setLoading(false);
			return;
		}

		setLoading(true);
		const results = refinement
			? await fetchAddressSuggestions(newValue.tekst, newValue.caretpos, refinement)
			: await fetchAddressSuggestions(newValue.tekst, newValue.caretpos);
		if (!isMountedRef.current || requestId !== requestIdRef.current) return;
		setLoading(false);

		const addresses = refinement ? results.filter((result) => {
			const address = extractAddress(result.type, result.data);
			return address?.adgangsadresseid === refinement.accessAddressId;
		}) : results;
		if (refinement && addresses.length === 1) {
			selectAddress(addresses[0]);
		} else {
			setOptions(addresses);
			setOpen(allowOpenRef.current);
			if (addresses.length === 0) setLookupError("Kunne ikke hente adressen. Vælg adressen igen.");
		}
	};

	return (
		<div className="address-autocomplete-root">
			<Autocomplete
				open={open}
				onOpen={() => {
					allowOpenRef.current = true;
					if (options.length > 0) setOpen(true);
				}}
				onClose={(_event, reason) => {
					if (keepOpenRef.current) {
						if (reason === "selectOption") {
							keepOpenRef.current = false;
							return;
						}
						keepOpenRef.current = false;
					}
					if (reason !== "selectOption") allowOpenRef.current = false;
					setOpen(false);
				}}
				value={value}
				options={options}
				loading={loading}
				loadingText="Søger..."
				noOptionsText="Ingen resultater"
				inputValue={inputValue}
				onInputChange={handleInputChange}
				onChange={handleChange}
				getOptionLabel={(option) => option.forslagstekst}
				filterOptions={(x) => x}
				isOptionEqualToValue={(option, value) => optionKey(option) === optionKey(value)}
				clearOnBlur={false}
				blurOnSelect={false}
				forcePopupIcon={false}
				data-testid="address-autocomplete"
				renderOption={(props, option) => (
					<li {...props} key={optionKey(option)} data-testid="suggestion-item">
						{option.forslagstekst}
					</li>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						label={placeholder}
						variant="outlined"
						error={!!lookupError}
						helperText={lookupError}
						FormHelperTextProps={{ role: "alert" }}
						onTouchStart={touchStart}
						inputProps={{
							...params.inputProps,
							"data-testid": "address-autocomplete-input",
							"aria-label": "Søg efter adresse",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{loading ? (
										<CircularProgress color="inherit" size={18} />
									) : null}
								</>
							),
						}}
					/>
				)}
				ListboxProps={{
					"data-testid": "suggestions-dropdown",
				} as React.HTMLAttributes<HTMLUListElement>}
			/>
		</div>
	);
};

export default AddressAutocomplete;
